import { CommonModule } from '@angular/common';
import { Component, effect, input, signal } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

@Component({
  selector: 'casa-doc-input-area-file',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './input-area-file.component.html',
  styleUrl: './input-area-file.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputAreaFileComponent,
      multi: true,
    },
  ],
})
export class InputAreaFileComponent implements ControlValueAccessor {
  private dragDepth = 0;

  accept = input<string>('*');
  multiple = input<boolean>(false);
  // disabled = input<boolean>(false);
  files = signal<File[]>([]);
  fileOver = signal<boolean>(false);

  constructor() {
    effect(
      () => {
        this.writeValue(this.files());
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isFileDrag(event)) {
      return;
    }

    this.dragDepth += 1;
    this.fileOver.set(true);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isFileDrag(event)) {
      return;
    }

    this.fileOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isFileDrag(event)) {
      return;
    }

    this.dragDepth = Math.max(0, this.dragDepth - 1);

    if (this.dragDepth === 0) {
      this.fileOver.set(false);
    }
  }

  onDrop(event: DragEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.dragDepth = 0;
    this.fileOver.set(false);

    if (!this.isFileDrag(event)) {
      return;
    }

    let files: File[] = [];
    if (event.dataTransfer.items) {
      for (const item of Array.from(event.dataTransfer.items)) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
    } else {
      for (const item of Array.from(event.dataTransfer.files)) {
        files.push(item);
      }
    }

    const acceptedExtensions = this.accept()
      .split(',')
      .map((ext) => ext.trim().toLowerCase());
    files = files.filter((file) => {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      return (
        acceptedExtensions.includes(fileExtension) ||
        acceptedExtensions.includes('*')
      );
    });

    this.files.update((prev) => [...prev, ...files]);
  }

  fileBrowseHandler(files: File[]) {
    for (const item of files) {
      this.files.update((prev) => [...prev, item]);
    }
  }

  onClearAll() {
    this.files.set([]);
  }

  writeValue(value: File[]): void {
    this.files.set(value);
    this.onChange(this.files());
  }
  onChange = (value: File[]) => {};

  onTouched = () => {};

  registerOnChange(fn: (value: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {}

  private isFileDrag(event: DragEvent): boolean {
    const dragTypes = event.dataTransfer?.types;
    return !!dragTypes && Array.from(dragTypes).includes('Files');
  }
}
